import { HttpErrorResponse } from '@angular/common/http';
import { ErrorHandler, inject, Service, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { NGXLogger } from 'ngx-logger';
import { ToastrService } from 'ngx-toastr';
import { environment } from 'src/environment/environment';
import { WINDOW } from '../../providers/window/window.provider';
import { AppRoutes } from '../../routes/common.routes';

@Service()
export class GlobalErrorHandler implements ErrorHandler {
  #injector: Injector = inject(Injector);
  #window = inject(WINDOW);
  #router: Router;
  #toastr: ToastrService;
  #logger: NGXLogger;

  handleError(error: any): void {
    let message: string;
    const isLocalhost = this.#window.location.host.includes('localhost');
    const stackTrace = error.stack || '';
    this.#toastr ??= this.#injector.get(ToastrService);
    this.#logger ??= this.#injector.get(NGXLogger);
    this.#router ??= this.#injector.get(Router);

    if (error instanceof HttpErrorResponse) {
      message = `Server Error: ${error.status} - ${error.message}`;
      if (error.error && typeof error.error === 'object') {
        if (error.error.message) {
          message = error.error.message;
        }
      } else if (typeof error.error === 'string') {
        message = `Server Error: ${error.status} - ${error.error}`;
      }
    } else if (error instanceof Error) {
      message = error.message;
    } else {
      message = JSON.stringify(error);
    }

    this.#logger.error('Global Error:', { message, stackTrace });

    if (!environment.production && isLocalhost) {
      this.#logger.error('Global Error:', error);
      this.#toastr.error('Something went wrong. Please try again.', 'Global Error', {
        closeButton: true,
        positionClass: 'toast-bottom-full-width',
      });
    } else {
      this.#logger.error('Global Error:', error);
    }
  }

  handleHttpError(error: HttpErrorResponse): void {
    switch (error.status) {
      case 401:
        this.#router.navigate([AppRoutes.login], {
          queryParams: { returnUrl: this.#router.url },
        });
        break;
      case 403:
        this.#router.navigate([AppRoutes.accessDenied]);
        break;

      case 404:
        this.#router.navigate([AppRoutes.notFound]);
        break;

      case 500:
        this.#router.navigate([AppRoutes.serverError]);
        break;

      default:
        this.#toastr.error('An unexpected error occurred. Please try again later.');
    }
  }

  logErrorToService(errorInfo: { message: string; stackTrace: string }): void {
    if (!environment.production) {
      return;
    }

    fetch('/api/errors', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(errorInfo),
    }).catch((logError) => {
      this.#logger.error('Failed to log error to service:', logError);
    });
  }
}
